import { getPredefinedGroupLabel } from "@common/constants/domains/accounting/accountGroups";
import { getForm1099BoxLabel } from "@common/constants/domains/accounting/form1099Box";
import { displayAccountLabel } from "@common/helpers/accounting";
import { neutralizeSpreadsheetFormula } from "@common/helpers/spreadsheet";
import { ChartOfAccountsColumn } from "@common/types/views/chartOfAccountsView";
import { ChartOfAccountsRow } from "./buildHierarchicalChartOfAccounts";

export type ExportCell = string | number;

export const DEFAULT_CHART_OF_ACCOUNTS_EXPORT_COLUMNS: ChartOfAccountsColumn[] =
    [
        ChartOfAccountsColumn.NAME,
        ChartOfAccountsColumn.TYPE,
        ChartOfAccountsColumn.SUBTYPE,
        ChartOfAccountsColumn.SOURCE,
        ChartOfAccountsColumn.ENTITIES,
    ];

const EXPORTABLE_COLUMN_HEADERS = new Map<ChartOfAccountsColumn, string>([
    [ChartOfAccountsColumn.NAME, "Account"],
    [ChartOfAccountsColumn.TYPE, "Type"],
    [ChartOfAccountsColumn.SUBTYPE, "Subtype"],
    [ChartOfAccountsColumn.DEBIT, "Debit"],
    [ChartOfAccountsColumn.CREDIT, "Credit"],
    [ChartOfAccountsColumn.BALANCE, "Balance"],
    [ChartOfAccountsColumn.SOURCE, "Source"],
    [ChartOfAccountsColumn.FORM_1099_BOX, "1099 box"],
    [ChartOfAccountsColumn.ENTITIES, "Entities"],
]);

export function getResolvedChartOfAccountsExportColumns(
    columns?: ChartOfAccountsColumn[],
): ChartOfAccountsColumn[] {
    if (!columns?.length) {
        return DEFAULT_CHART_OF_ACCOUNTS_EXPORT_COLUMNS;
    }
    const resolved = [...new Set(columns)].filter((column) =>
        EXPORTABLE_COLUMN_HEADERS.has(column),
    );
    return resolved.length
        ? resolved
        : DEFAULT_CHART_OF_ACCOUNTS_EXPORT_COLUMNS;
}

const NESTING_INDENT = "    ";
const STATUS_HEADER = "Status";

type EntityNamesById = Map<number, string | null>;

interface BuildChartOfAccountsExportTableParams {
    rows: ChartOfAccountsRow[];
    columns: ChartOfAccountsColumn[];
    entityNamesById: EntityNamesById;
    showAccountNumbers: boolean;
}

type RowLabeler = (row: ChartOfAccountsRow) => string;

const getRowLabel: RowLabeler = (row) => {
    const predefinedGroup = row.section ?? row.predefinedType;
    if (predefinedGroup) {
        return getPredefinedGroupLabel(predefinedGroup);
    }
    if (row.classification) {
        return row.classification;
    }
    if (row.group) {
        return row.group.name;
    }
    return row.account?.name ?? "";
};

const getRowLabelWithCode: RowLabeler = (row) => {
    if (row.account) {
        return displayAccountLabel(row.account, true);
    }
    return getRowLabel(row);
};

function getEntityNames(
    row: ChartOfAccountsRow,
    entityNamesById: EntityNamesById,
): string {
    return (row.entityIds ?? [])
        .map((entityId) => entityNamesById.get(entityId))
        .filter((name): name is string => !!name)
        .join(", ");
}

function getCellValue({
    column,
    row,
    depth,
    entityNamesById,
    labelRow,
}: {
    column: ChartOfAccountsColumn;
    row: ChartOfAccountsRow;
    depth: number;
    entityNamesById: EntityNamesById;
    labelRow: RowLabeler;
}): ExportCell {
    switch (column) {
        case ChartOfAccountsColumn.NAME:
            return NESTING_INDENT.repeat(depth) + labelRow(row);
        case ChartOfAccountsColumn.TYPE:
            return row.account?.type ?? "";
        case ChartOfAccountsColumn.SUBTYPE:
            return row.account?.subtype ?? "";
        case ChartOfAccountsColumn.DEBIT:
            return row.account?.debit ?? row.groupDebit ?? "";
        case ChartOfAccountsColumn.CREDIT:
            return row.account?.credit ?? row.groupCredit ?? "";
        case ChartOfAccountsColumn.BALANCE:
            return row.account?.balance ?? row.groupBalance ?? "";
        case ChartOfAccountsColumn.SOURCE:
            if (!row.account) {
                return "";
            }
            return row.account.isManuallyCreated ? "Custom" : "Kick";
        case ChartOfAccountsColumn.FORM_1099_BOX:
            if (!row.account) {
                return "";
            }
            return getForm1099BoxLabel(row.account.form1099Box) ?? "";
        case ChartOfAccountsColumn.ENTITIES:
            if (row.classification) {
                return "";
            }
            return getEntityNames(row, entityNamesById);
        default:
            return "";
    }
}

function getStatus(row: ChartOfAccountsRow): string {
    if (!row.account) {
        return "";
    }
    return row.account.isDisabled ? "Archived" : "Active";
}

/**
 * Builds a WYSIWYG export table (headers + rows) mirroring the on-screen
 * nested Chart of Accounts: same rows, same order, same visible columns.
 * Nesting is conveyed by indenting the account column, and a trailing Status
 * column flags archived accounts.
 */
export function buildChartOfAccountsExportTable({
    rows,
    columns,
    entityNamesById,
    showAccountNumbers,
}: BuildChartOfAccountsExportTableParams): ExportCell[][] {
    const exportColumns = columns.filter((column) =>
        EXPORTABLE_COLUMN_HEADERS.has(column),
    );

    const headers = [
        ...exportColumns.map(
            (column) => EXPORTABLE_COLUMN_HEADERS.get(column) ?? "",
        ),
        STATUS_HEADER,
    ];
    const table: ExportCell[][] = [headers];
    const labelRow = showAccountNumbers ? getRowLabelWithCode : getRowLabel;

    function visit(currentRows: ChartOfAccountsRow[], depth: number) {
        for (const row of currentRows) {
            const cells = exportColumns.map((column) =>
                neutralizeSpreadsheetFormula(
                    getCellValue({
                        column,
                        row,
                        depth,
                        entityNamesById,
                        labelRow,
                    }),
                ),
            );
            table.push([...cells, getStatus(row)]);
            if (row.accounts && row.accounts.length > 0) {
                visit(row.accounts, depth + 1);
            }
        }
    }

    visit(rows, 0);
    return table;
}
